import React from 'react';

import { cn } from '@/lib/utils';

import type { PositionType } from '../types';
import { estimateLiquidationPrice, priceMoveFraction } from '../utils/pnl';

interface TradeProgressBarProps {
    entryPrice: number;
    currentPrice: number;
    positionType: PositionType;
    /** Position leverage; the bar is scaled in ROI on collateral. Default 1x. */
    leverage?: number;
    /** Real liquidation price (backend). Estimated from leverage if omitted. */
    liquidationPrice?: number | null;
    /** Optional take-profit target price; mirrors the liq distance if omitted. */
    takeProfitPrice?: number | null;
}

const TICK_COUNT = 9;

/** ROI on collateral (percent) at a given price. */
function roiAt(
    entry: number,
    price: number,
    positionType: PositionType,
    leverage: number,
): number {
    return priceMoveFraction(entry, price, positionType) * leverage * 100;
}

/** Price that produces a given ROI on collateral. */
function priceForRoi(
    entry: number,
    roiPercent: number,
    positionType: PositionType,
    leverage: number,
): number {
    const frac = roiPercent / 100 / leverage;
    return positionType === 'LONG' ? entry * (1 + frac) : entry * (1 - frac);
}

const TradeProgressBar: React.FC<TradeProgressBarProps> = ({
    entryPrice,
    currentPrice,
    positionType,
    leverage = 1,
    liquidationPrice,
    takeProfitPrice,
}) => {
    const lev = Number.isFinite(leverage) && leverage > 0 ? leverage : 1;

    const roi = roiAt(entryPrice, currentPrice, positionType, lev);
    const isProfit = roi >= 0;

    // Liquidation anchors the bottom of the bar; use the real liq price when we
    // have it, otherwise estimate from leverage. Every caller passes a
    // liquidationPrice that is already exchange-real or maintenance-aware, so
    // this last resort has no market to read a maintenance rate from (0) and
    // only decides the bar's scale, never a number the user reads.
    const liqPrice =
        liquidationPrice ??
        estimateLiquidationPrice(entryPrice, positionType, lev, 0) ??
        entryPrice;
    const rawLiqRoi = roiAt(entryPrice, liqPrice, positionType, lev);
    const liqRoi =
        Number.isFinite(rawLiqRoi) && rawLiqRoi < 0 ? rawLiqRoi : -100;

    // Take-profit anchors the top. With no TP armed there is nothing to anchor
    // to, so the scale mirrors the liq distance — a bound, NOT a target: the
    // label reads "—" rather than naming that price, which is how a position
    // with a 2829.7 take-profit came to show "TP 2575.11" (entry + the exact
    // distance down to liquidation) on the chart.
    const hasTp =
        takeProfitPrice !== null &&
        takeProfitPrice !== undefined &&
        takeProfitPrice > 0;
    const tpRoi = hasTp
        ? roiAt(entryPrice, takeProfitPrice, positionType, lev)
        : Math.abs(liqRoi);
    const tpPrice = hasTp
        ? takeProfitPrice
        : priceForRoi(entryPrice, tpRoi, positionType, lev);

    const range = tpRoi - liqRoi || 1;
    const cursorRatio = Math.max(0, Math.min(1, (roi - liqRoi) / range));
    // CSS top%: 0% = top of bar (take profit), 100% = bottom (liquidation)
    const cursorTopPct = (1 - cursorRatio) * 100;
    const entryRatio = Math.max(0, Math.min(1, (0 - liqRoi) / range));
    const entryTopPct = (1 - entryRatio) * 100;

    // Adaptive decimal places based on price magnitude
    const decimals = entryPrice >= 1 ? 2 : entryPrice >= 0.01 ? 4 : 6;
    const fmt = (v: number) => v.toFixed(decimals);

    // Fill spans from the entry line to the cursor
    const fillTop = Math.min(cursorTopPct, entryTopPct);
    const fillHeight = Math.abs(cursorTopPct - entryTopPct);

    const profitColor = 'var(--success)';
    const lossColor = 'var(--destructive)';
    const activeColor = isProfit ? profitColor : lossColor;

    const ticks = Array.from({ length: TICK_COUNT }, (_, i) => {
        const pct = (i / (TICK_COUNT - 1)) * 100;
        return pct;
    });

    return (
        <div className="pointer-events-none absolute bottom-6 left-2 top-6 z-10 flex w-4 md:w-9 flex-col md:bottom-8 md:left-3 md:top-8">
            {/* TP Label */}
            <div className="mb-1 flex items-center gap-1">
                <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    TP
                </span>
                <span className="font-mono text-[8px] tabular-nums text-success">
                    {hasTp ? fmt(tpPrice) : '—'}
                </span>
            </div>

            {/* Bar Container */}
            <div className="relative flex-1 border border-border/60 bg-card/80">
                {/* Tick marks along the left edge */}
                {ticks.map((pct) => (
                    <div
                        key={pct}
                        className="absolute left-0 h-px w-1.5 bg-muted-foreground/20"
                        style={{ top: `${pct}%` }}
                    />
                ))}

                {/* P&L fill region (entry → cursor) */}
                <div
                    className={cn(
                        'absolute inset-x-0 transition-all duration-150',
                        isProfit ? 'bg-success/15' : 'bg-destructive/15',
                    )}
                    style={{
                        top: `${fillTop}%`,
                        height: `${fillHeight}%`,
                    }}
                />

                {/* Entry line (center) */}
                <div
                    className="absolute inset-x-0 border-t border-dashed border-muted-foreground/70"
                    style={{ top: `${entryTopPct}%` }}
                >
                    <span className="absolute left-full ml-1 -translate-y-1/2 whitespace-nowrap font-mono text-[9px] tabular-nums text-muted-foreground">
                        {fmt(entryPrice)}
                    </span>
                </div>

                {/* Cursor indicator */}
                <div
                    className="absolute inset-x-0 transition-all duration-150"
                    style={{ top: `${cursorTopPct}%` }}
                >
                    {/* Horizontal line */}
                    <div
                        className="absolute inset-x-0 h-px"
                        style={{ backgroundColor: activeColor }}
                    />
                    {/* Diamond marker */}
                    <div
                        className={cn(
                            'absolute left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border',
                            isProfit
                                ? 'bg-success/30'
                                : 'bg-destructive/30',
                        )}
                        style={{ borderColor: activeColor }}
                    />
                    {/* Current price readout */}
                    <span
                        className={cn(
                            'absolute left-full ml-2 -translate-y-1/2 whitespace-nowrap font-mono text-[9px] font-bold tabular-nums',
                            isProfit ? 'text-success' : 'text-destructive',
                        )}
                    >
                        {fmt(currentPrice)}
                    </span>
                </div>
            </div>

            {/* LQ Label */}
            <div className="mt-1 flex items-center gap-1">
                <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    LQ
                </span>
                <span className="font-mono text-[8px] tabular-nums text-destructive">
                    {fmt(liqPrice)}
                </span>
            </div>
        </div>
    );
};

export default TradeProgressBar;
